import axios from 'axios'
import store from '../init-redux'
import { buildLoginContinuePath } from '../services/continueNavigation'
import { clearPersistedSession } from '../services/sessionCleanup'
import { ssoClient } from '../services/SigninSetup'

export interface Response<T> {
  data: T,
}

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_URL_API,
})

const RETRY_FLAG = 'retryAttemptedByAuthInterceptor'

const syncToken = (token?: string | null) => {
  const currentToken = store.getState().app.token
  const nextToken = token || undefined

  if (currentToken !== nextToken) {
    store.dispatch({ type: 'app/setToken', payload: nextToken })
  }
}

const resetSessionAndRedirectToLogin = () => {
  syncToken(undefined)
  clearPersistedSession()

  const loginPath = `${process.env.PUBLIC_URL || ''}/login`
  const isLoginPage = window.location.pathname === loginPath

  if (!isLoginPage) {
    window.location.replace(buildLoginContinuePath())
  }
}

axiosInstance.interceptors.request.use(async (config) => {
  const cfg = config
  const token = await ssoClient.getToken()

  syncToken(token)

  if (token) {
    cfg.headers = cfg.headers ?? {}
    cfg.headers.Authorization = `Bearer ${token}`
  }

  return cfg
})

axiosInstance.interceptors.response.use((response) => response, async (error) => {
  const isUnauthorized = error.response?.status === 401
  const isBadgeAuthenticationError = error.response?.data?.origin === 'badge_authentication'
  const originalRequest = error.config
  const isAuthenticatedRequest = !!originalRequest?.headers?.Authorization
  const hasAlreadyRetried = !!originalRequest?.[RETRY_FLAG]

  if (isUnauthorized && !isBadgeAuthenticationError && isAuthenticatedRequest && !hasAlreadyRetried) {
    originalRequest[RETRY_FLAG] = true

    const refreshed = await ssoClient.refreshToken()

    if (refreshed) {
      const token = await ssoClient.getToken()

      if (token) {
        syncToken(token)
        originalRequest.headers = originalRequest.headers ?? {}
        originalRequest.headers.Authorization = `Bearer ${token}`
        return axiosInstance(originalRequest)
      }
    }
  }

  if (isUnauthorized && !isBadgeAuthenticationError) {
    resetSessionAndRedirectToLogin()
  }

  return Promise.reject(error)
})

export default axiosInstance
